import { getFCMToken, onMessageListener, requestNotificationPermission, isMessagingAvailable } from '../config/firebase';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.nistylez.com';

class NotificationService {
  constructor() {
    this.token = null;
    this.isInitialized = false;
    this.messagingSupported = false;
  }

  async initialize() {
    try {
      // Check if messaging is available (it might not be in Safari or private mode)
      this.messagingSupported = isMessagingAvailable();
      
      if (!this.messagingSupported) {
        console.warn('⚠️ Push notifications are not available in this browser. App will work without them.');
        return false;
      }

      const hasPermission = await requestNotificationPermission();
      if (!hasPermission) {
        console.log('ℹ️ Notification permission not granted. App will work without notifications.');
        return false;
      }

      this.token = await getFCMToken();
      if (this.token) {
        await this.sendTokenToBackend(this.token);
        this.isInitialized = true;
        console.log('✅ Notification service initialized successfully');
        return true;
      }
      return false;
    } catch (error) {
      console.warn('⚠️ Error initializing notification service:', error.message);
      console.log('ℹ️ App will continue to work without push notifications');
      return false;
    }
  }

  async sendTokenToBackend(token) {
    try {
      console.log('🔄 Sending FCM token to backend...');
      const user = JSON.parse(localStorage.getItem('user'));
      const authToken = localStorage.getItem('token') || localStorage.getItem('customerToken');
      
      if (!user) {
        console.log('❌ No user found in localStorage');
        return;
      }
      
      if (!authToken) {
        console.log('❌ No auth token found in localStorage');
        return;
      }

      console.log('👤 User found:', user.email);
      console.log('🔑 FCM Token to send:', token.substring(0, 50) + '...');
      console.log('🔑 Auth Token found:', authToken.substring(0, 20) + '...');

      const response = await axios.post(`${API_BASE_URL}/api/notifications/register-token`, {
        token: token,
        platform: 'web',
        appType: 'customer'
      }, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('✅ FCM token sent to backend successfully:', response.data);
    } catch (error) {
      console.error('❌ Error sending FCM token to backend:', error);
      console.error('Error details:', error.response?.data || error.message);
    }
  }

  async listenForMessages() {
    if (!this.messagingSupported) {
      console.log('ℹ️ Messaging not supported - skipping message listener');
      return;
    }
    
    try {
      const payload = await onMessageListener();
      if (payload) {
        this.handleForegroundMessage(payload);
      }
    } catch (error) {
      console.warn('⚠️ Error listening for messages:', error.message);
    }
  }

  handleForegroundMessage(payload) {
    const notification = {
      title: payload.notification?.title || 'New Notification',
      body: payload.notification?.body || 'You have a new message',
      data: payload.data,
      timestamp: new Date().toISOString()
    };

    window.dispatchEvent(new CustomEvent('notificationReceived', { 
      detail: notification 
    }));
  }

  // Fetch notifications from backend
  async fetchNotifications() {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const authToken = localStorage.getItem('token') || localStorage.getItem('customerToken');
      if (!user || !authToken) return [];

      const response = await axios.get(`${API_BASE_URL}/api/customer/notifications`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('🔍 Notification API Response:', response.data);

      if (response.data && response.data.success) {
        // Backend returns: { success: true, data: { notifications: [...], pagination: {...}, unreadCount: number } }
        const notifications = response.data.data?.notifications || response.data.notifications || [];
        console.log('📋 Parsed notifications:', notifications.length);
        return notifications;
      }
      return [];
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
  }

  // Mark notification as read
  async markAsRead(notificationId) {
    try {
      const user = JSON.parse(localStorage.getItem('user'));
      const authToken = localStorage.getItem('token') || localStorage.getItem('customerToken');
      if (!user || !authToken) return false;

      const response = await axios.put(`${API_BASE_URL}/api/customer/notifications/${notificationId}/read`, {}, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });

      return response.data && response.data.success;
    } catch (error) {
      console.error('Error marking notification as read:', error);
      return false;
    }
  }

  getToken() { return this.token; }
  isServiceInitialized() { return this.isInitialized; }
}

const notificationService = new NotificationService();

// Expose to global window for debugging
if (typeof window !== 'undefined') {
  window.notificationService = notificationService;
}

export default notificationService;
